'use client'

import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { forwardRef, useId } from 'react'

const checkboxVariants = cva(
  'peer flex shrink-0 items-center justify-center rounded border ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:border-neutral-300 disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'data-[state=checked]:border-primary data-[state=checked]:bg-white data-[state=checked]:text-primary-foreground border-neutral-500',
        secondary:
          'data-[state=checked]:border-secondary data-[state=checked]:bg-white data-[state=checked]:text-secondary-foreground border-neutral-500',
        neutral:
          'data-[state=checked]:border-neutral-900 data-[state=checked]:bg-white data-[state=checked]:text-neutral-900 border-neutral-500',
      },
      size: {
        default: 'h-5 w-5 sm:h-5 sm:w-5',
        small: 'h-4 w-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

const indicatorVariants = cva('rounded-[2px] text-current', {
  variants: {
    variant: {
      primary: 'bg-primary',
      secondary: 'bg-secondary',
      neutral: 'bg-neutral-900',
    },
    size: {
      default: 'h-3 w-3',
      small: 'h-2.5 w-2.5',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
})

interface CheckboxProps
  extends
    Omit<
      React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
      'title'
    >,
    VariantProps<typeof checkboxVariants> {
  titleClassName?: string
  wrapperClassName?: string
  indicatorClassName?: string
  title?: React.ReactNode
}

const Checkbox = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      size,
      variant,
      title,
      titleClassName,
      wrapperClassName,
      indicatorClassName,
      ...props
    },
    ref
  ) => {
    const id = useId()

    return (
      <div className={cn('flex items-center', wrapperClassName)}>
        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(checkboxVariants({ variant, size, className }))}
          {...props}
          id={props?.id || id}
        >
          <CheckboxPrimitive.Indicator
            className={cn(
              indicatorVariants({ variant, size }),
              indicatorClassName
            )}
          />
        </CheckboxPrimitive.Root>
        {title && (
          <label
            htmlFor={props?.id || id}
            className={cn(
              'ml-2 font-sans text-sm font-normal leading-[15px] text-neutral-500 disabled:text-neutral-300 peer-enabled:cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70 md:text-base',
              size !== 'small' && 'text-base',
              titleClassName
            )}
          >
            {title}
          </label>
        )}
      </div>
    )
  }
)
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
